import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-fiscal-reports',
  templateUrl: './fiscal-reports.component.html',
  styleUrls: ['./fiscal-reports.component.scss']
})
export class FiscalReportsComponent implements OnInit {

  @Input() storeId: string = '';
  @Input() storeName: string = '';

  // Sub-tab
  activeSection: string = 'x-report';

  // X-Report
  xReport: any = null;
  isLoadingX: boolean = false;
  xReportError: string = '';

  // Z-Reports list
  zReports: any[] = [];
  isLoadingZ: boolean = false;
  zReportError: string = '';
  zCurrentPage: number = 1;
  zTotalPages: number = 1;
  zTotal: number = 0;
  zPageSize: number = 20;

  // Z-Report detail modal
  showZDetail: boolean = false;
  selectedZReport: any = null;
  isLoadingZDetail: boolean = false;

  isGeneratingZ: boolean = false;

  // Auto Z-Report failure banner
  autoZReportFailed: boolean = false;
  autoZReportLastError: string = '';
  autoZReportLastRunAt: Date | null = null;

  Math = Math;

  constructor(
    private ecommerceService: EcommerceService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.storeId) {
      this.loadXReport();
      this.loadZReports();
      this.loadAutoZReportStatus();
    }
  }

  loadAutoZReportStatus(): void {
    this.ecommerceService.getStoreSettings(this.storeId).subscribe(
      (res: any) => {
        let store: any = null;
        if (res.code === 200 && res.data?.store) {
          store = res.data.store;
        } else if (res.response?.success && res.response?.data?.store) {
          store = res.response.data.store;
        }
        const zCfg = store?.autoZReportConfig;
        if (zCfg && zCfg.lastRunStatus === 'failed') {
          this.autoZReportFailed = true;
          this.autoZReportLastError = zCfg.lastRunError || 'Unknown error';
          this.autoZReportLastRunAt = zCfg.lastRunAt ? new Date(zCfg.lastRunAt) : null;
        } else {
          this.autoZReportFailed = false;
        }
      },
      () => {
        // Silently ignore — this is an optional status check
      }
    );
  }

  setSection(section: string): void {
    this.activeSection = section;
  }

  // X-Report
  loadXReport(): void {
    this.isLoadingX = true;
    this.xReportError = '';

    this.ecommerceService.getXReport(this.storeId).subscribe(
      (res: any) => {
        const data = res?.response?.data ?? res?.data ?? null;
        this.xReport = data?.report ?? data ?? null;
        this.isLoadingX = false;
      },
      (error: any) => {
        this.xReportError = error?.error?.response?.message || 'Failed to load X-Report';
        this.toastr.error(this.xReportError);
        this.isLoadingX = false;
      }
    );
  }

  confirmGenerateZReport(): void {
    Swal.fire({
      title: 'Generate Z-Report?',
      text: 'Are you sure you want to close the current period and generate a Z-Report? This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, close period',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545'
    }).then(result => {
      if (result.isConfirmed) {
        this.generateZReport();
      }
    });
  }

  generateZReport(): void {
    this.isGeneratingZ = true;
    this.ecommerceService.generateZReport(this.storeId).subscribe(
      (res: any) => {
        this.toastr.success('Z-Report generated successfully');
        this.isGeneratingZ = false;
        this.loadXReport();
        this.zCurrentPage = 1;
        this.loadZReports();
        this.setSection('z-reports');
      },
      (error: any) => {
        const msg = error?.error?.response?.message || 'Failed to generate Z-Report';
        this.toastr.error(msg);
        this.isGeneratingZ = false;
      }
    );
  }

  // Z-Reports list
  loadZReports(): void {
    this.isLoadingZ = true;
    this.zReportError = '';

    this.ecommerceService.getZReports(this.storeId, this.zCurrentPage, this.zPageSize).subscribe(
      (res: any) => {
        const data = res?.response?.data ?? res?.data ?? null;
        if (data) {
          this.zReports = data.reports || [];
          this.zTotal = data.total ?? 0;
          this.zTotalPages = data.totalPages ?? 1;
          this.zCurrentPage = data.page ?? 1;
        } else {
          this.zReports = [];
        }
        this.isLoadingZ = false;
      },
      (error: any) => {
        this.zReportError = error?.error?.response?.message || 'Failed to load Z-Reports';
        this.toastr.error(this.zReportError);
        this.isLoadingZ = false;
      }
    );
  }

  onZPageChange(page: number): void {
    this.zCurrentPage = page;
    this.loadZReports();
  }

  // Z-Report detail
  viewZReport(report: any): void {
    this.showZDetail = true;
    this.isLoadingZDetail = true;
    this.selectedZReport = null;

    this.ecommerceService.getZReport(this.storeId, report._id).subscribe(
      (res: any) => {
        const data = res?.response?.data ?? res?.data ?? null;
        this.selectedZReport = data?.report ?? data ?? report;
        this.isLoadingZDetail = false;
      },
      (error: any) => {
        // Fall back to the list row data if detail fetch fails
        this.selectedZReport = report;
        this.isLoadingZDetail = false;
      }
    );
  }

  closeZDetail(): void {
    this.showZDetail = false;
    this.selectedZReport = null;
  }

  printDetail(): void {
    if (!this.selectedZReport) { return; }
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      this.toastr.error('Could not open print window. Please allow popups for this site.');
      return;
    }
    const reportHtml = this.buildReportHtml(this.selectedZReport);
    printWindow.document.write(reportHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  }

  private buildReportHtml(report: any): string {
    const storeName = this.storeName || 'Store';
    const reportDate = report.closedAt || report.periodEnd;
    const dateStr = reportDate
      ? new Date(reportDate).toLocaleDateString('da-DK', { day: '2-digit', month: 'long', year: 'numeric' })
      : '—';
    const periodStartStr = report.periodStart
      ? new Date(report.periodStart).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '—';
    const periodEndStr = report.periodEnd
      ? new Date(report.periodEnd).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '—';

    const fmt = (amount: number | undefined | null): string => {
      if (amount == null) { return '0,00'; }
      return new Intl.NumberFormat('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
    };

    const fmtInt = (n: number | undefined | null): string => {
      if (n == null) { return '0'; }
      return new Intl.NumberFormat('da-DK').format(Math.round(n));
    };

    const voidedCount = report.voidedCount ?? 0;
    const voidedTotal = report.voidedTotal ?? 0;
    const returnCount = report.returnCount ?? report.refundCount ?? 0;
    const returnTotal = report.returnTotal ?? report.refundTotal ?? 0;
    const uniqueCustomerCount = report.uniqueCustomerCount ?? 0;
    const totalItemsCount = report.totalItemsCount ?? 0;
    const transactionCount = report.transactionCount ?? 0;
    const averageOrderValue = transactionCount > 0 ? (report.grandTotalGross ?? 0) / transactionCount : 0;
    const averageItemsPerOrder = transactionCount > 0 ? totalItemsCount / transactionCount : 0;

    const paymentRows = (report.salesByPaymentMethod || []).map((row: any) => {
      const methodLabel = this.formatPaymentMethod(row.paymentMethod);
      const rCount = row.returnCount ?? 0;
      const rTotal = row.returnTotal ?? 0;
      return '<tr>'
        + '<td>' + methodLabel + '</td>'
        + '<td class="num">' + fmtInt(row.transactionCount) + '</td>'
        + '<td class="num">' + fmt(row.totalAmount) + '</td>'
        + '<td class="num">' + fmtInt(rCount) + '</td>'
        + '<td class="num">' + fmt(rTotal) + '</td>'
        + '</tr>';
    }).join('');

    const paymentTotals = (report.salesByPaymentMethod || []).reduce(
      (acc: any, row: any) => {
        acc.count += row.transactionCount ?? 0;
        acc.total += row.totalAmount ?? 0;
        acc.rCount += row.returnCount ?? 0;
        acc.rTotal += row.returnTotal ?? 0;
        return acc;
      }, { count: 0, total: 0, rCount: 0, rTotal: 0 }
    );

    const currencies = report.salesByCurrency || [];
    const hasMultiCurrency = currencies.length > 1 || currencies.some((c: any) => c.currency !== 'DKK');
    let currencySection = '';
    if (hasMultiCurrency) {
      const currencyRows = currencies.map((c: any) =>
        '<tr>'
        + '<td>' + c.currency + '</td>'
        + '<td class="num">' + fmtInt(c.transactionCount) + '</td>'
        + '<td class="num">' + fmt(c.totalAmount) + '</td>'
        + '</tr>'
      ).join('');
      const currencyTotal = currencies.reduce((s: number, c: any) => s + (c.transactionCount ?? 0), 0);
      const currencyAmtTotal = currencies.reduce((s: number, c: any) => s + (c.totalAmount ?? 0), 0);
      currencySection = '<h3>Valuta</h3>'
        + '<table class="data-table">'
        + '<thead><tr><th>Valuta</th><th class="num">Antal</th><th class="num">Beløb</th></tr></thead>'
        + '<tbody>' + currencyRows + '</tbody>'
        + '<tfoot><tr>'
        + '<td><strong>I alt valuta</strong></td>'
        + '<td class="num"><strong>' + fmtInt(currencyTotal) + '</strong></td>'
        + '<td class="num"><strong>' + fmt(currencyAmtTotal) + '</strong></td>'
        + '</tr></tfoot>'
        + '</table>';
    }

    const movements = report.cashMovements || [];
    let cashRows = '';
    if (movements.length > 0) {
      cashRows = movements.map((mv: any) => {
        const ts = mv.timestamp
          ? new Date(mv.timestamp).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })
          : '—';
        const typeLabel = mv.type === 'in' ? 'Indbetaling' : mv.type === 'out' ? 'Udbetaling' : (mv.type || '—');
        return '<tr>'
          + '<td>' + typeLabel + '</td>'
          + '<td class="num">' + fmt(mv.amount) + '</td>'
          + '<td>' + (mv.note || '—') + '</td>'
          + '<td>' + ts + '</td>'
          + '</tr>';
      }).join('');
    }

    const cashierRows = (report.salesByManager || []).map((row: any) =>
      '<tr>'
      + '<td>' + (row.managerName || row.managerId || '—') + '</td>'
      + '<td class="num">' + fmtInt(row.transactionCount) + '</td>'
      + '<td class="num">' + fmt(row.totalAmount) + '</td>'
      + '</tr>'
    ).join('');

    const taxRows = (report.taxSummary || []).map((row: any) =>
      '<tr>'
      + '<td>' + new Intl.NumberFormat('da-DK', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(row.taxRate ?? 0) + '%</td>'
      + '<td class="num">' + fmt(row.netAmount) + '</td>'
      + '<td class="num">' + fmt(row.taxAmount) + '</td>'
      + '<td class="num">' + fmt(row.grossAmount) + '</td>'
      + '</tr>'
    ).join('');

    const cashMovementsTable = movements.length > 0
      ? '<br><table class="data-table">'
        + '<thead><tr><th>Type</th><th class="num">Beløb</th><th>Note</th><th>Tidspunkt</th></tr></thead>'
        + '<tbody>' + cashRows + '</tbody>'
        + '</table>'
      : '';

    const css = '@page { size: A4; margin: 15mm 15mm 20mm 15mm; }'
      + '* { box-sizing: border-box; margin: 0; padding: 0; }'
      + 'body { font-family: Arial, Helvetica, sans-serif; font-size: 9pt; color: #000; background: #fff; }'
      + '.report-header { display: flex; justify-content: space-between; align-items: flex-start;'
      + '  margin-bottom: 6pt; padding-bottom: 4pt; border-bottom: 1px solid #000; }'
      + '.report-header .left { font-size: 11pt; font-weight: bold; }'
      + '.report-header .left .store-name { font-size: 13pt; font-weight: bold; }'
      + '.report-header .right { text-align: right; font-size: 9pt; }'
      + '.report-title { font-size: 16pt; font-weight: bold; margin-bottom: 4pt; }'
      + '.report-date-line { font-size: 10pt; font-weight: bold; margin-bottom: 10pt; }'
      + '.two-col { display: flex; gap: 20pt; margin-bottom: 8pt; }'
      + '.two-col .col-left { flex: 1; } .two-col .col-right { flex: 1; }'
      + '.summary-row { display: flex; justify-content: space-between; padding: 1pt 0; font-size: 9pt; }'
      + '.summary-row .value { font-weight: bold; text-align: right; }'
      + '.summary-row.total-row { border-top: 1px solid #000; margin-top: 2pt; padding-top: 2pt; font-weight: bold; }'
      + 'h3 { font-size: 9pt; font-weight: bold; margin-bottom: 3pt; padding-top: 6pt; border-top: 1px solid #ccc; }'
      + 'table.data-table { width: 100%; border-collapse: collapse; font-size: 8.5pt; margin-bottom: 6pt; }'
      + 'table.data-table th { background: #f0f0f0; border-bottom: 1px solid #999;'
      + '  padding: 2pt 4pt; text-align: left; font-weight: bold; }'
      + 'table.data-table th.num, table.data-table td.num { text-align: right; }'
      + 'table.data-table td { padding: 1.5pt 4pt; border-bottom: 1px solid #e0e0e0; }'
      + 'table.data-table tfoot td { border-top: 1px solid #000; border-bottom: none;'
      + '  font-weight: bold; background: #f5f5f5; }'
      + '.horizontal-line { border-top: 1px solid #000; margin: 4pt 0; }'
      + '@media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }';

    const header = '<!DOCTYPE html><html lang="da"><head>'
      + '<meta charset="UTF-8">'
      + '<meta name="viewport" content="width=device-width, initial-scale=1.0">'
      + '<title>Finansrapport \u2014 ' + storeName + ' \u2014 Rapport #' + (report.reportNumber ?? '—') + '</title>'
      + '<style>' + css + '</style>'
      + '</head><body>';

    const reportHeader = '<div class="report-header">'
      + '<div class="left">'
      + '<div class="report-title">Finansrapport</div>'
      + '<div class="store-name">' + storeName + '</div>'
      + '</div>'
      + '<div class="right">'
      + '<div>' + dateStr + '</div>'
      + '<div>Side 1</div>'
      + '<div>Rapport #' + (report.reportNumber ?? '—') + '</div>'
      + '</div>'
      + '</div>';

    const dateLine = '<div class="report-date-line">Dato: ' + periodStartStr + ' \u2014 ' + periodEndStr + '</div>';

    const twoColSection = '<div class="two-col">'
      + '<div class="col-left">'
      + '<div class="summary-row"><span class="label">Bruttosalg</span><span class="value">' + fmt(report.grandTotalGross) + '</span></div>'
      + '<div class="summary-row"><span class="label">Antal kunder</span><span class="value">' + fmtInt(uniqueCustomerCount) + '</span></div>'
      + '<div class="summary-row"><span class="label">Gns. pr. kunde</span><span class="value">' + fmt(averageOrderValue) + '</span></div>'
      + '<div class="summary-row"><span class="label">Antal varer</span><span class="value">' + fmtInt(totalItemsCount) + '</span></div>'
      + '<div class="summary-row"><span class="label">Gns. pr. vare</span><span class="value">' + fmt(averageItemsPerOrder) + '</span></div>'
      + '</div>'
      + '<div class="col-right">'
      + '<table class="data-table">'
      + '<thead><tr><th></th><th class="num">Antal</th><th class="num">Bel\u00f8b</th></tr></thead>'
      + '<tbody>'
      + '<tr><td>Slet bon (annulleret)</td><td class="num">' + fmtInt(voidedCount) + '</td><td class="num">' + fmt(voidedTotal) + '</td></tr>'
      + '<tr><td>Varesalg retur</td><td class="num">' + fmtInt(returnCount) + '</td><td class="num">' + fmt(returnTotal) + '</td></tr>'
      + '<tr><td>Rabatter</td><td class="num">\u2014</td><td class="num">\u2014</td></tr>'
      + '</tbody>'
      + '<tfoot><tr><td>Antal transaktioner i alt</td><td class="num">' + fmtInt(transactionCount) + '</td><td class="num">' + fmt(report.grandTotalGross) + '</td></tr></tfoot>'
      + '</table>'
      + '</div>'
      + '</div>';

    const paymentSection = '<h3>PENGE \u2014 Betalingsmetoder</h3>'
      + '<table class="data-table">'
      + '<thead><tr>'
      + '<th>Betalingsmetode</th>'
      + '<th class="num">Antal</th>'
      + '<th class="num">Registreret</th>'
      + '<th class="num">Retur antal</th>'
      + '<th class="num">Retur bel\u00f8b</th>'
      + '</tr></thead>'
      + '<tbody>' + (paymentRows || '<tr><td colspan="5" style="text-align:center">\u2014</td></tr>') + '</tbody>'
      + '<tfoot><tr>'
      + '<td><strong>I alt penge</strong></td>'
      + '<td class="num"><strong>' + fmtInt(paymentTotals.count) + '</strong></td>'
      + '<td class="num"><strong>' + fmt(paymentTotals.total) + '</strong></td>'
      + '<td class="num"><strong>' + fmtInt(paymentTotals.rCount) + '</strong></td>'
      + '<td class="num"><strong>' + fmt(paymentTotals.rTotal) + '</strong></td>'
      + '</tr></tfoot>'
      + '</table>';

    const cardCashbackTotal = report.cardCashbackTotal ?? 0;
    const cardCashbackCount = report.cardCashbackCount ?? 0;
    const cardCashbackLabel = this.translate.instant('FISCAL.CARD_CASHBACK');

    const cashSection = '<h3>Kassebeholdning</h3>'
      + '<div class="summary-row">'
      + '<span class="label">\u00c5bningssaldo (kasseoptælling)</span>'
      + '<span class="value">' + fmt(report.openingFloat ?? 0) + '</span>'
      + '</div>'
      + cashMovementsTable
      + '<div class="summary-row">'
      + '<span class="label">' + cardCashbackLabel + '</span>'
      + '<span class="value">' + fmt(cardCashbackTotal) + ' (' + fmtInt(cardCashbackCount) + ')</span>'
      + '</div>'
      + '<div class="summary-row total-row" style="margin-top: 4pt;">'
      + '<span class="label">Afsluttende beholdning</span>'
      + '<span class="value">' + fmt(report.closingBalance ?? 0) + '</span>'
      + '</div>';

    const cashierSection = '<h3>Salg pr. kasserer</h3>'
      + '<table class="data-table">'
      + '<thead><tr><th>Kasserer</th><th class="num">Antal</th><th class="num">Bel\u00f8b</th></tr></thead>'
      + '<tbody>' + (cashierRows || '<tr><td colspan="3" style="text-align:center">\u2014</td></tr>') + '</tbody>'
      + '<tfoot><tr>'
      + '<td><strong>I alt</strong></td>'
      + '<td class="num"><strong>' + fmtInt(transactionCount) + '</strong></td>'
      + '<td class="num"><strong>' + fmt(report.grandTotalGross) + '</strong></td>'
      + '</tr></tfoot>'
      + '</table>';

    const taxSection = '<h3>Momsoversigt</h3>'
      + '<table class="data-table">'
      + '<thead><tr><th>Momssats</th><th class="num">Netto</th><th class="num">Moms</th><th class="num">Brutto</th></tr></thead>'
      + '<tbody>' + (taxRows || '<tr><td colspan="4" style="text-align:center">\u2014</td></tr>') + '</tbody>'
      + '<tfoot><tr>'
      + '<td><strong>I alt</strong></td>'
      + '<td class="num"><strong>' + fmt(report.grandTotalNet) + '</strong></td>'
      + '<td class="num"><strong>' + fmt(report.grandTotalTax) + '</strong></td>'
      + '<td class="num"><strong>' + fmt(report.grandTotalGross) + '</strong></td>'
      + '</tr></tfoot>'
      + '</table>';

    const footer = '<div class="horizontal-line"></div>'
      + '<div style="font-size: 8pt; color: #666; text-align: center; margin-top: 6pt;">'
      + 'Lukket af: ' + (report.closedByManagerName || '\u2014') + ' \u00b7 ' + dateStr
      + '</div></body></html>';

    return header + reportHeader + dateLine + twoColSection
      + paymentSection + currencySection
      + cashSection + cashierSection + taxSection + footer;
  }

  getAverageOrderValue(): number {
    if (!this.selectedZReport) { return 0; }
    const count = this.selectedZReport.transactionCount ?? 0;
    return count > 0 ? (this.selectedZReport.grandTotalGross ?? 0) / count : 0;
  }

  hasForeignCurrency(): boolean {
    return (this.selectedZReport?.salesByCurrency || []).some((c: any) => c.currency !== 'DKK');
  }

  downloadSaft(report: any): void {
    const periodStart = report.periodStart ? report.periodStart.split('T')[0] : '';
    const periodEnd = report.periodEnd ? report.periodEnd.split('T')[0] : '';
    this.ecommerceService.getSaftExport(this.storeId, periodStart, periodEnd).subscribe(
      (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `SAFT-${this.storeId}-${periodStart}-${periodEnd}.xml`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      (error: any) => {
        const handleError = (msg: string) => {
          this.toastr.error(msg || 'SAF-T export failed');
        };
        if (error?.error instanceof Blob) {
          error.error.text().then((text: string) => {
            try {
              const parsed = JSON.parse(text);
              handleError(parsed?.response?.message || parsed?.message || text);
            } catch {
              handleError(text);
            }
          }).catch(() => handleError('SAF-T export failed'));
        } else {
          handleError(error?.error?.response?.message || error?.message || 'SAF-T export failed');
        }
      }
    );
  }

  // Formatting helpers
  formatCurrency(amount: number, currency: string = 'DKK'): string {
    if (amount == null) { return '—'; }
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: currency || 'DKK'
    }).format(amount);
  }

  formatCardCashbackSummary(report: any): string {
    if (!report) { return '—'; }
    const total = report.cardCashbackTotal ?? 0;
    const count = report.cardCashbackCount ?? 0;
    return `${this.formatCurrency(total)} (${count})`;
  }

  formatPaymentMethod(method: string): string {
    const map: any = {
      card: 'Card',
      card_terminal: 'Card Terminal',
      cash: 'Cash',
      mobile: 'Mobile',
      wallet: 'Wallet'
    };
    return map[method] || method;
  }

  trackByReportId(index: number, report: any): string {
    return report._id || index;
  }
}
